import { useCallback, useEffect, useRef, useState } from 'react';
import useBackofficeAxios from '../../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../../hooks/useBackofficeOwnerAuth';
import { useAppSelector } from '../../../redux/helper';
import { getProductCategoriesInformation } from '../../../api/backofficeProducts';
import { toFiniteNumber } from '../../../utils/backofficeProductView';

export type InventorySubCategory = { name: string; products: number };

export type InventoryCategory = {
  /** Empty for products with no category. */
  category: string;
  products: number;
  subCategories: InventorySubCategory[];
};

export const parseInventoryCategories = (
  res: any,
): InventoryCategory[] | null => {
  if (res?.status !== 200) return null;
  const body = res?.data;
  if (!body || typeof body !== 'object') return null;
  if (body.status != null && Number(body.status) !== 200) return null;
  if (!Array.isArray(body.data)) return null;
  return body.data.map((row: any) => ({
    category: String(row?.category ?? ''),
    products: toFiniteNumber(row?.products) ?? 0,
    subCategories: Array.isArray(row?.subCategories)
      ? row.subCategories
          .map((sub: any) => ({
            name: String(sub?.name ?? ''),
            products: toFiniteNumber(sub?.products) ?? 0,
          }))
          .filter((sub: InventorySubCategory) => sub.name)
      : [],
  }));
};

/**
 * Categories with subcategories and product counts, for the filter sheet and
 * picker. Nothing is fetched while `enabled` is false.
 */
export const useInventoryCategories = ({ enabled = true } = {}) => {
  const axiosBackoffice = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();
  const locationVersion = useAppSelector(s => s.location.locationVersion);
  const [categories, setCategories] = useState<InventoryCategory[] | null>(
    null,
  );
  const [failed, setFailed] = useState(false);
  const requestRef = useRef(0);

  const reload = useCallback(async () => {
    if (!enabled || !owner?.accessToken) return;
    const requestId = ++requestRef.current;
    setFailed(false);
    try {
      const res = await getProductCategoriesInformation(
        axiosBackoffice as any,
        { accessToken: owner.accessToken },
      );
      if (requestId !== requestRef.current) return;
      const parsed = parseInventoryCategories(res);
      setCategories(parsed);
      setFailed(parsed == null);
    } catch {
      if (requestId === requestRef.current) {
        setCategories(null);
        setFailed(true);
      }
    }
  }, [axiosBackoffice, enabled, owner?.accessToken]);

  useEffect(() => {
    void reload();
  }, [reload, locationVersion]);

  return { categories, failed, reload };
};
